import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ArrowLeft, Scale } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';

import { BACKEND_URL } from '../config/api';

const defaultTermsContent = `
<p class="lead">Last updated: January 2026</p>

<p><strong>OnSiteHand is operated by ONSITEHAND LIMITED, a private limited company registered in England and Wales under company number 17186065, with its registered office at Unit A449, 4-6 Greatorex Street, London, E1 5NF, United Kingdom. In these Terms, "OnSiteHand", "we", "us" and "our" refer to ONSITEHAND LIMITED.</strong></p>

<p>These Terms of Service ("Terms") govern your use of OnSiteHand ("the platform"), an online marketplace that helps employers and tradespeople find and contact each other. OnSiteHand is operated by ONSITEHAND LIMITED, registered in England and Wales under company number 17186065, registered office Unit A449, 4-6 Greatorex Street, London, E1 5NF, United Kingdom. By using OnSiteHand you agree to these Terms.</p>

<p><strong>Free marketplace notice:</strong> OnSiteHand is free to use for tradespeople and employers. Searching for tradespeople, viewing profiles, messaging and making contact through the platform are free.</p>

<h2>1. The service</h2>
<p>OnSiteHand provides an online marketplace and matching platform. We publish worker profiles, let employers search, and let the two sides communicate inside the platform. We are not party to any employment, subcontracting or service arrangement that users enter into after making contact.</p>

<h2>2. OnSiteHand does not employ tradespeople</h2>
<p>OnSiteHand does not employ the tradespeople listed on the platform. Any employment, subcontracting or service arrangement that follows from using OnSiteHand is strictly between the employer/client and the worker.</p>

<h2>3. Employer responsibilities</h2>
<p>As an employer or hiring party you are responsible for:</p>
<ul>
  <li>Deciding whether a worker is suitable for your specific needs;</li>
  <li>Carrying out your own checks on suitability, Right to Work, credentials, insurance, tax status and legal compliance where these apply to how you engage the worker;</li>
  <li>Communicating professionally and respectfully with workers;</li>
  <li>Paying workers directly for work performed on the terms you agree with them;</li>
  <li>Leaving honest and fair reviews;</li>
  <li>Complying with all employment, tax, health and safety and consumer-protection laws that apply to how you engage the worker.</li>
</ul>

<h2>4. Worker responsibilities</h2>
<p>As a worker you are responsible for:</p>
<ul>
  <li>Providing accurate profile information, including qualifications, certifications and experience;</li>
  <li>Keeping your profile and availability up to date;</li>
  <li>Responding to enquiries honestly and professionally;</li>
  <li>Delivering work in line with what you agree with the hiring party;</li>
  <li>Holding appropriate insurance and certification for your trade where required;</li>
  <li>Complying with the regulations that apply to your trade.</li>
</ul>

<h2>5. Verification badges</h2>
<p>Where OnSiteHand displays a verification badge or verification status next to a worker's profile or credential, that badge relates only to evidence OnSiteHand has actually reviewed for that specific credential. A badge is not a guarantee of workmanship, conduct, availability, legal status or ongoing suitability. OnSiteHand does not perform Right to Work verification, DBS checks, identity verification or employment-status determinations. Users remain responsible for any checks or legal obligations that apply to their own hiring, contracting or working arrangements.</p>

<h2>6. No guarantees</h2>
<p>OnSiteHand does not guarantee:</p>
<ul>
  <li>That you will find work or hire a suitable tradesperson;</li>
  <li>The quality, safety or legality of any work carried out after users meet through the platform;</li>
  <li>The accuracy of information provided by other users;</li>
  <li>That the platform will be available without interruption.</li>
</ul>

<h2>7. Messaging, offers and contact details</h2>
<p>Messages, offers, counteroffers and acceptances sent through OnSiteHand are communications between users and may create binding arrangements between those users. OnSiteHand is not a party to those arrangements. Workers may choose a contact preference that controls whether approved employers can reveal their phone number through the platform. We may record security and abuse-prevention information about contact-detail access events — see our <a href="/privacy">Privacy Policy</a>.</p>

<h2>8. Pricing</h2>
<p>OnSiteHand is free to use for tradespeople and employers.</p>

<h2>9. Acceptable use</h2>
<p>You must not:</p>
<ul>
  <li>Use the platform for illegal purposes;</li>
  <li>Harass, threaten or abuse other users;</li>
  <li>Spam or send unsolicited messages;</li>
  <li>Create multiple accounts to evade restrictions;</li>
  <li>Scrape, copy or misuse platform data;</li>
  <li>Impersonate others or provide false information.</li>
</ul>

<h2>10. Suspension and termination</h2>
<p>We may suspend or terminate your account for breaches of these Terms, suspected fraud or abuse, or at our reasonable discretion. We will act proportionately.</p>

<h2>11. Limitation of liability</h2>
<p>To the maximum extent permitted by law, OnSiteHand is not liable for any disputes between users, for any work performed or not performed by users, or for any indirect, incidental or consequential damages. Our total aggregate liability is limited to the greater of (i) any amount you have paid us in the twelve months preceding the claim, which may be zero, and (ii) one hundred pounds (£100). Nothing in these Terms limits any statutory consumer rights you have under UK law.</p>

<h2>12. Disputes</h2>
<p>Disputes between users should be resolved directly between the parties. OnSiteHand is not a party to any arrangement reached between employers and workers.</p>

<h2>13. Governing law</h2>
<p>These Terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>

<h2>14. Changes to these Terms</h2>
<p>We may update these Terms. Continued use of OnSiteHand after a change constitutes acceptance of the updated Terms.</p>

<h2>15. Contact</h2>
<p>For questions about these Terms, contact us at:</p>
<ul>
  <li>Email: <a href="mailto:legal@onsitehand.co.uk">legal@onsitehand.co.uk</a></li>
  <li>Company: ONSITEHAND LIMITED (company number 17186065)</li>
  <li>Registered office: Unit A449, 4-6 Greatorex Street, London, E1 5NF, United Kingdom <em>(registered office only — please contact us online for support)</em></li>
</ul>

<p><em>Some wording in this document may be reviewed by a solicitor from time to time. Where legal interpretation is required (for example around consumer protection, data retention or specific statutory obligations), professional legal advice should be sought.</em></p>
`;

export default function TermsOfService() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/pages/terms`);
      if (response.data.sections?.main?.content) {
        setContent(response.data.sections.main.content);
      }
    } catch (error) {
      // Fallback to defaults
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div 
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/')}
            >
              <Building2 className="h-6 w-6 text-orange-500" />
              <span className="text-xl font-bold text-slate-900">OnsiteHand</span>
            </div>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary text-sm"
            data-testid="login-btn"
          >
            Sign In
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <Scale className="h-8 w-8 text-orange-500" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" data-testid="page-title">
            Terms of Service
          </h1>
          <p className="text-slate-600">Last updated: January 2026</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          {loading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-6 bg-slate-200 rounded w-1/3"></div>
              <div className="h-4 bg-slate-200 rounded w-full"></div>
              <div className="h-4 bg-slate-200 rounded w-full"></div>
              <div className="h-4 bg-slate-200 rounded w-2/3"></div>
            </div>
          ) : (
            <div 
              className="prose prose-slate max-w-none [&>h2]:text-xl [&>h2]:font-bold [&>h2]:text-slate-900 [&>h2]:mt-8 [&>h2]:mb-4 [&>h2:first-child]:mt-0 [&>p]:text-slate-600 [&>p]:mb-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:text-slate-600"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content || defaultTermsContent) }}
              data-testid="terms-content"
            />
          )}
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>
            Also see our{' '}
            <button onClick={() => navigate('/privacy')} className="text-orange-500 hover:underline">
              Privacy Policy
            </button>
            {' '}and{' '}
            <button onClick={() => navigate('/faq')} className="text-orange-500 hover:underline">
              FAQ
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
